/** @file AboutDialog: editor build, server version and build, contracts tag, Babylon version, and links. */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useEditorStore } from '../state/useEditorStore';
import { Button } from '../ui-kit/Button';
import { Modal } from '../ui-kit/Modal';
import { buildInfo } from './buildInfo';
import type { HealthReply } from './serverInfo';
import { serverInfo } from './serverInfo';
import { useApi } from './useApi';

/** The design spec in the repository (the authority for every ruling). */
const SPEC_URL = 'https://github.com/ibankzero55/pose3d/blob/main/docs/specs/2026-10-21-pose3d-design.md';

type Server = { state: 'none' } | { state: 'loading' } | { state: 'error'; text: string } | { state: 'ok'; health: HealthReply };

/** `GET /api/v1/health` of `url` (null = no server), once. */
function useServer(url: string | null, fetchFn: typeof fetch): Server {
  const [server, setServer] = useState<Server>(url === null ? { state: 'none' } : { state: 'loading' });
  useEffect(() => {
    if (url === null) return undefined;
    let live = true;
    serverInfo(url, fetchFn).then((health) => { if (live) setServer({ state: 'ok', health }); }, (e: unknown) => {
      if (live) setServer({ state: 'error', text: e instanceof Error ? e.message : String(e) });
    });
    return () => { live = false; };
  }, [url, fetchFn]);
  return server;
}

const time = (iso: string): string => (iso === '' ? 'unknown' : iso.replace('T', ' ').replace(/\.\d+Z$/, 'Z'));

/** The server rows; a server that does not report `build` is an older deploy. */
function serverRows(server: Server): [string, string][] {
  switch (server.state) {
    case 'none': return [['Server', 'none (memory store)']];
    case 'loading': return [['Server', 'checking...']];
    case 'error': return [['Server', `not reachable (${server.text})`]];
    case 'ok': {
      const b = server.health.build;
      return [['Server version', server.health.version], ['Server build', b === undefined ? 'not reported (older deploy)' : `${b.sha} at ${time(b.at)}`]];
    }
  }
}

/** The About dialog. `fetchFn` is the transport for the health request (a test passes a fake). */
export function AboutDialog({ onClose, fetchFn = fetch }: { onClose: () => void; fetchFn?: typeof fetch }): ReactElement {
  const store = useEditorStore();
  const api = useApi();
  const build = buildInfo();
  const server = useServer(api?.url ?? null, fetchFn);
  const rows: [string, string][] = [
    ['Editor build', build.sha], ['Editor built', time(build.at)], ...serverRows(server),
    ['Contracts', build.contracts], ['Babylon', build.babylon],
  ];
  const guides = (): void => { store.getState().setShell({ dialog: 'guides' }); };
  return (
    <Modal title="About Pose3D" onClose={onClose}
      footer={<><Button onClick={guides} disabled={api === null}>Guides</Button><Button variant="primary" onClick={onClose}>Close</Button></>}>
      <p className="p3-muted" style={{ margin: 0 }}>A 3D puppet that draws as pixel art; agents and humans edit the same puppet.</p>
      <dl className="p3-about">
        {rows.map(([name, value]) => <div key={name} className="p3-about-row"><dt>{name}</dt><dd className="p3-mono">{value}</dd></div>)}
      </dl>
      <div className="p3-row">
        <a href={SPEC_URL} target="_blank" rel="noreferrer">Design spec</a>
        {api === null ? null : <a href={`${api.url.replace(/\/+$/, '')}/api/v1/guides`} target="_blank" rel="noreferrer">Guides (server)</a>}
      </div>
    </Modal>
  );
}
